import type { MouseEvent } from 'react';
import type { BundleVariantsType } from '@/modals/modules/balances/+ShopModal/types/bundles';
import type { BadgeOffer } from '@/queries/bundles/bundleOffers.schema';
import * as VisuallyHidden from '@radix-ui/react-visually-hidden';
import { useTranslations } from 'next-intl';
import { useState } from 'react';
import BottomSheetModal from '@/components/shared/BottomSheetModal';
import { DialogDescription, DialogTitle } from '@/components/ui/Dialog/dialog';
import { useIsMobile } from '@/hooks/useIsMobile';
import { DepositStep } from '@/modals/modules/balances/+ShopModal/types/DepositStep';
import { normalizeBadges } from '@/queries/bundles/bundleOffers.schema';
import { cn } from '@/utils/cn';
import { BundleBadge } from './components/BundleBadge';

interface Props {
  dailyBonusUpTo?: number;
  variant: BundleVariantsType;
  badges?: BadgeOffer | BadgeOffer[] | null;
  setDepositStep: (step: DepositStep) => void;
}

const getOrderedBadges = (badges?: BadgeOffer | BadgeOffer[] | null) => {
  const normalizedBadges = normalizeBadges(badges);
  const systemBadges = normalizedBadges.filter(({ type }) => type === 'system');
  const promoBadges = normalizedBadges.filter(({ type }) => type === 'promo');

  return [...systemBadges, ...promoBadges];
};

export const BundleBadges = ({ dailyBonusUpTo, badges, variant, setDepositStep }: Props) => {
  const t = useTranslations('bundles');
  const isMobile = useIsMobile();
  const [isOpenSheetModal, setIsOpenSheetModal] = useState(false);
  const dailyBonusText = `${t('up-to')} ${dailyBonusUpTo || 0} ${t('daily')}`;
  const isShowDailyBadge = !!dailyBonusUpTo;
  const isRegularBundle = variant === 'regular';
  const isVipBundle = variant === 'vip';
  const orderedBadges = getOrderedBadges(badges);

  const handleClick = (e?: MouseEvent) => {
    if (isMobile) {
      setIsOpenSheetModal(true);
    } else {
      setDepositStep(DepositStep.DAILY_BUNDLE_INFO);
    }
    e?.stopPropagation();
  };

  return (
    <div className={
      cn('absolute flex gap-2 h-5 left-4 z-1', isRegularBundle ? '-top-3' : '-top-2.5')
    }
    >
      {orderedBadges.map((badge, index) => (
        <BundleBadge key={`${badge.type ?? 'badge'}-${badge.label}-${index}`} badge={badge} />
      ))}
      {
        isVipBundle && (
          <BundleBadge isVip={true} />
        )
      }
      {
        isShowDailyBadge && (
          <BundleBadge
            onClick={handleClick}
            dailyBonusText={dailyBonusText}
          />
        )
      }

      <BottomSheetModal open={isOpenSheetModal} onOpenChange={setIsOpenSheetModal}>
        <VisuallyHidden.Root>
          <DialogTitle className="sr-only">
            {t('daily-bonus-modal.title')}
          </DialogTitle>
          <DialogDescription className="sr-only">
            {t('daily-bonus-modal.description')}
          </DialogDescription>
        </VisuallyHidden.Root>
        <p className="h5-semibold text-white mb-3">{t('bundle-purchases-activate-7-daily-bonus-card-activations-one-per-day')}</p>
        <p className="h5-semibold text-white mb-3">{t('card-activations-from-multiple-purchases-stack-and-up-to-9-cards-can-be-activated-per-day-if-all-bundle-cards-are-purchased')}</p>
        <p className="h5-semibold text-white mb-3">{t('if-the-same-bundle-is-purchased-multiple-times-the-number-of-activations-stacks-rewards-vary-and-the-maximum-amount-is-displayed-on-the-bundle-card')}</p>
      </BottomSheetModal>
    </div>
  );
};
